import { type ReactNode, useEffect } from "react";
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  Link,
} from "@tanstack/react-router";
import * as Sentry from '@sentry/tanstackstart-react';
import { fetchSessionProfile } from '@/server/auth';
import { getAllFeatureFlags } from '@/server/featureFlags';
import type { RouterContext } from '@/router';
import appCss from "@/styles/app.css?url";
import { PopupProvider } from "@/components/PopupProvider";
import { ToastProvider } from "@/components/ui/toast";
import { initMixpanel, identifyUser, resetUser } from '@/lib/mixpanel';

// Base URL for constructing absolute URLs for social sharing
// In production, this should be set via environment variable
const SITE_URL = import.meta.env.VITE_SITE_URL || "https://wayra.cc";

const defaultMeta = {
  title: "Wayra - Discover Cycling Events & Rides Near You",
  description:
    "Discover and explore cycling events near you. Find rides, races, and cycling adventures with Wayra.",
  image: `${SITE_URL}/og-image.jpg`,
};

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location }) => {
    // Skip profile fetch on auth callback — code exchange hasn't happened yet
    // and getUser() may clear cookies needed for the PKCE flow
    if (location.pathname === '/auth/callback') {
      const featureFlags = await getAllFeatureFlags({data: null});
      return { profile: null, featureFlags };
    }
    const profile = await fetchSessionProfile();
    const featureFlags = await getAllFeatureFlags({data: profile});
    if (profile) {
      Sentry.setUser({ id: profile.id, email: profile.email });
    } else {
      Sentry.setUser(null);
    }
    return { profile, featureFlags };
  },
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      {
        title: defaultMeta.title,
      },
      {
        name: "description",
        content: defaultMeta.description,
      },
      // Open Graph meta tags
      {
        property: "og:type",
        content: "website",
      },
      {
        property: "og:site_name",
        content: "Wayra",
      },
      {
        property: "og:url",
        content: SITE_URL,
      },
      {
        property: "og:title",
        content: defaultMeta.title,
      },
      {
        property: "og:description",
        content: defaultMeta.description,
      },
      {
        property: "og:image",
        content: defaultMeta.image,
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        property: "og:image:alt",
        content: defaultMeta.title,
      },
      {
        property: "og:locale",
        content: "en_US",
      },
      // Twitter Card meta tags
      {
        name: "twitter:card",
        content: "summary_large_image",
      },
      {
        name: "twitter:title",
        content: defaultMeta.title,
      },
      {
        name: "twitter:description",
        content: defaultMeta.description,
      },
      {
        name: "twitter:image",
        content: defaultMeta.image,
      },
      {
        name: "twitter:image:alt",
        content: defaultMeta.title,
      },
      // Additional SEO meta tags
      {
        name: "robots",
        content: "index, follow",
      },
      {
        name: "theme-color",
        content: "#0f172a",
      },
      {
        name: "apple-mobile-web-app-title",
        content: "Wayra",
      },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
        suppressHydrationWarning: true,
      },
      {
        rel: "icon",
        type: "image/png",
        href: "/favicon/favicon-96x96.png",
        sizes: "96x96",
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/favicon/favicon.svg",
      },
      {
        rel: "shortcut icon",
        href: "/favicon/favicon.ico",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/favicon/apple-touch-icon.png",
      },
      {
        rel: "manifest",
        href: "/favicon/site.webmanifest",
      },
    ],
  }),
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function NotFoundComponent() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center px-6">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-slate-900 mb-4">404</h1>
        <p className="text-xl text-slate-600 mb-8">
          Oops! The page you're looking for doesn't exist.
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-3 text-base font-medium transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to Home
        </Link>
      </div>
    </div>
  );
}

function RootComponent() {
  const { profile } = Route.useRouteContext();

  useEffect(() => {
    initMixpanel();
  }, []);

  useEffect(() => {
    if (profile) {
      identifyUser(profile.id, {
        $email: profile.email,
        $first_name: profile.firstName,
        $last_name: profile.lastName,
        $name: profile.displayName ?? profile.firstName,
        $avatar: profile.avatarUrl,
      });
    } else {
      resetUser();
    }
  }, [profile]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <RootDocument>
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100 flex items-center justify-center px-6">
            <div className="text-center">
              <h1 className="text-6xl font-bold text-slate-900 mb-4">Oops</h1>
              <p className="text-xl text-slate-600 mb-8">
                Something went wrong. Our team has been notified.
              </p>
              <button
                type="button"
                onClick={resetError}
                className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-3 text-base font-medium transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </RootDocument>
      )}
    >
      <RootDocument>
        <ToastProvider position="bottom-center">
          <PopupProvider>
            <Outlet />
          </PopupProvider>
        </ToastProvider>
      </RootDocument>
    </Sentry.ErrorBoundary>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
